<script setup>
import { ref, computed } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import LiveChatCampaignForm from 'dashboard/components-next/Campaigns/Pages/CampaignPage/LiveChatCampaign/LiveChatCampaignForm.vue';

const props = defineProps({
  selectedCampaign: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const liveChatCampaignFormRef = ref(null);
const confirmCloseRef = ref(null);
const saving = ref(false);
const saveError = ref('');

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isUpdatingCampaign = computed(
  () => saving.value || uiFlags.value.isUpdating
);

const isInvalidForm = computed(
  () => liveChatCampaignFormRef.value?.isSubmitDisabled
);

const selectedCampaignId = computed(() => props.selectedCampaign.id);

const updateCampaign = async campaignDetails => {
  if (isUpdatingCampaign.value) return;
  saving.value = true;
  saveError.value = '';
  try {
    await store.dispatch('campaigns/update', {
      id: selectedCampaignId.value,
      ...campaignDetails,
    });

    useAlert(t('CAMPAIGN.LIVE_CHAT.EDIT.FORM.API.SUCCESS_MESSAGE'));
    dialogRef.value.close();
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAMPAIGN.LIVE_CHAT.EDIT.FORM.API.ERROR_MESSAGE');
    saveError.value = errorMessage;
  } finally {
    saving.value = false;
  }
};

const handleSubmit = () => {
  if (isInvalidForm.value || isUpdatingCampaign.value) return;
  updateCampaign(liveChatCampaignFormRef.value.prepareCampaignDetails());
};

const handleRequestClose = () => {
  if (isUpdatingCampaign.value) return;
  if (liveChatCampaignFormRef.value?.isDirty?.()) confirmCloseRef.value?.open();
  else dialogRef.value?.close();
};
const confirmAbandon = () => {
  if (isUpdatingCampaign.value) return;
  confirmCloseRef.value?.close();
  dialogRef.value?.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('CAMPAIGN.LIVE_CHAT.EDIT.TITLE')"
    :is-loading="isUpdatingCampaign"
    :disable-confirm-button="isUpdatingCampaign || isInvalidForm"
    width="2xl"
    overflow-y-auto
    confirm-before-close
    @request-close="handleRequestClose"
    @close="saveError = ''"
    @confirm="handleSubmit"
  >
    <p v-if="saveError" role="alert" class="text-sm text-n-ruby-11">
      {{ saveError }}
    </p>
    <fieldset class="m-0 min-w-0 border-0 p-0" :disabled="isUpdatingCampaign">
      <LiveChatCampaignForm
        ref="liveChatCampaignFormRef"
        mode="edit"
        :selected-campaign="selectedCampaign"
        :show-action-buttons="false"
        @submit="handleSubmit"
      />
    </fieldset>
  </Dialog>
  <Dialog
    ref="confirmCloseRef"
    type="alert"
    :title="t('CAMPAIGN.LIVE_CHAT.CREATE.ABANDON.TITLE')"
    :description="t('CAMPAIGN.LIVE_CHAT.CREATE.ABANDON.DESCRIPTION')"
    :confirm-button-label="t('CAMPAIGN.LIVE_CHAT.CREATE.ABANDON.CONFIRM')"
    :cancel-button-label="t('CAMPAIGN.LIVE_CHAT.CREATE.ABANDON.CANCEL')"
    @confirm="confirmAbandon"
  />
</template>
